import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { TablePage, TableRequest } from './settingsDiagnosticActions'

/**
 * Easeedesk > AI Analizer Actions > AI Analizer Actions (v4/api,
 * `App\Settings\Diagnostic\AiAnalizerActions`). Forma paginii e inghetata
 * in `settings/diagnostic/__fixtures__/aiAnalizerActions.json`.
 */
const BASE = '/settings/diagnostic/ai_analizer'

export interface AiAnalizerPage {
  providerName: string
  actionTypes: { ID: number; type_name: string }[]
  categories: { ID: number; category_name: string }[]
  all_ticket_types: { ID: number; type_name: string; short_name: string }[]
  allCompaniesSp: { ID: number; name: string }[]
}

export interface AiCondition {
  reference: string
  operator: 'AND' | 'OR'
  action_condition: string
  value: string
  rating: string
  scope: string
}

export interface AiCommand {
  type: string
  id: string
  message: string
  time: string
  restart: string
  timeout: string
  scheduled: string
  mandatory_to_run: string
}

export interface AiActionForm {
  action_name: string
  action_type: string
  run_only_on_ticket_types: string[]
  run_only_on_companies: string[]
  default_for_all_sp: string
  os: string
  category: string
  subcategory: string
  operation: string
  eligible_for_adam_intop: boolean
  status: boolean
  condition: AiCondition[]
  commands: AiCommand[]
}

export interface AiActionDetails {
  action: Record<string, unknown>
  own: boolean
  conditions: Record<string, unknown>[]
  commands: Record<string, unknown>[]
  category: number
  subcategory: number
  operation: number
  run_only_on_ticket_types: string[]
  run_only_on_companies: string[]
}

export interface GroupedCommand {
  ID: number
  command_title: string
  group_id: number | string
  group_name: string
}

function params(r: TableRequest): string {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p.toString()
}

export function aiAnalizerPageQuery() {
  return queryOptions({ queryKey: ['settings', 'diagnostic', 'ai_analizer'], queryFn: () => apiGet<AiAnalizerPage>(BASE) })
}

export function aiAnalizerTableQuery(r: TableRequest) {
  return queryOptions({
    queryKey: ['settings', 'diagnostic', 'ai_analizer', 'table', r],
    queryFn: () => apiGet<TablePage<Record<string, unknown>>>(`${BASE}/table?${params(r)}`),
  })
}

export function aiAnalizerActionQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'diagnostic', 'ai_analizer', 'one', id],
    queryFn: () => apiGet<AiActionDetails>(`${BASE}/${id}`),
    enabled: id > 0,
  })
}

export function aiCommandsQuery(type: number) {
  return queryOptions({
    queryKey: ['settings', 'diagnostic', 'ai_analizer', 'commands', type],
    queryFn: () => apiGet<{ commands: GroupedCommand[] }>(`${BASE}/commands/${type}`),
    enabled: [1, 2, 5, 6, 7].includes(type),
  })
}

/** Campurile formularului vechi, nume cu nume (`create_ai_action` / `update_ai_action`). */
export function aiActionFields(form: AiActionForm): Record<string, PostValue> {
  const fields: Record<string, PostValue> = {
    action_name: form.action_name,
    action_type: form.action_type,
    default_for_all_sp: form.default_for_all_sp,
    os: form.os,
    category: form.category,
    subcategory: form.subcategory,
    operation: form.operation,
    run_only_on_ticket_types: form.run_only_on_ticket_types,
    run_only_on_companies: form.run_only_on_companies,
    condition: form.condition.map((c) => ({ ...c })),
    commands: form.commands.map((k) => ({ ...k })),
  }
  if (form.eligible_for_adam_intop) fields.eligible_for_adam_intop = 'on'
  if (form.status) fields.status = 'on'
  return fields
}

export function createAiAction(form: AiActionForm) {
  return apiPost<{ status: string; id: number }>(BASE, aiActionFields(form))
}

export function updateAiAction(id: number, form: AiActionForm) {
  return apiPost<{ status: string; id: number }>(`${BASE}/${id}`, aiActionFields(form))
}

export function removeAiAction(id: number) {
  return apiPost<{ status: string; id: number }>(`${BASE}/${id}/remove`, {})
}
